import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';
import { u2000SessionHasOrganization } from '../u2000Core/tools/U2000SystemScopeAccess';
import { u3203ExtractSparseOverrides } from './utils/u3203MissionParameterUtils';

const LOGICALUNIT_URL = 'U3203';

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
  REVISIONS: (id: string | number) => string;
  REVISION: (id: string | number, revisionNumber: number) => string;
  RESTORE: (id: string | number, revisionNumber: number) => string;
  DEFAULT_PROMPTS: (id: string | number) => string;
  ORG_CONFIG: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
  REVISIONS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/revisions`,
  REVISION: (id: string | number, revisionNumber: number) => `${LOGICALUNIT_URL}/${id}/revisions/${revisionNumber}`,
  RESTORE: (id: string | number, revisionNumber: number) => `${LOGICALUNIT_URL}/${id}/restore/${revisionNumber}`,
  DEFAULT_PROMPTS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/defaultPrompts`,
  ORG_CONFIG: (id: string | number) => `${LOGICALUNIT_URL}/${id}/orgConfig`,
};

const SERVER_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const U3203_MAX_ACTIVE_DEFAULT_PROMPTS = 25;
export const U3203_DEFAULT_PROMPT_LABEL_MAX = 120;

export type U3203MissionCategory = 'extraction' | 'assistant' | 'analyse';

export interface U3203TemplateVariableDefinition {
  name: string;
  placeholder: string;
  description: string;
}

export interface U3203InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode: string;
  categories: U3203MissionCategory[];
  templateVariableDefinitions: U3203TemplateVariableDefinition[];
}

export interface U3203ProviderRef {
  id: string;
  code: string;
  name: string;
}

export interface U3203ModelRef {
  id: string;
  code: string;
  name: string;
}

export interface U3203CorpusRef {
  id: string;
  code: string;
  title: string;
}

export interface U3203DefaultPrompt {
  id: string;
  labelFr: string;
  labelEn: string;
  promptFr: string;
  promptEn: string;
  sortOrder: number;
  active: boolean;
}

export type U3203DefaultPromptItemInput = Omit<U3203DefaultPrompt, 'id'> & { id?: string };

export interface U3203DefaultPromptsReplaceBody {
  items: U3203DefaultPromptItemInput[];
}

export interface U3203OrgConfigReplaceBody {
  defaultProviderId?: string | null;
  defaultModelId?: string | null;
  parameterOverrides?: Record<string, unknown>;
}

export interface U3203AiMissionSearchRow {
  id: string;
  code: string;
  title: string;
  category: U3203MissionCategory;
  linkedLogicalUnit?: string | null;
  active: boolean;
  systemScope: boolean;
  revisionNumber: number;
  corpusId?: string | null;
  version: number;
}

export interface U3203AiMission {
  id: string;
  code: string;
  title: string;
  description: string | null;
  systemPrompt: string;
  userPromptTemplate: string | null;
  templateVariables?: string[];
  category: U3203MissionCategory;
  linkedLogicalUnit: string | null;
  active: boolean;
  systemScope: boolean;
  revisionNumber: number;
  defaultProvider: U3203ProviderRef | null;
  defaultModel: U3203ModelRef | null;
  corpusId?: string | null;
  corpus?: U3203CorpusRef | null;
  parameterOverrides?: Record<string, unknown>;
  defaultPrompts?: U3203DefaultPrompt[];
  version: number;
}

export type U3203AiMissionFormRow = Omit<
  U3203AiMission,
  'description' | 'userPromptTemplate' | 'defaultProvider' | 'defaultModel' | 'corpus'
> & {
  description: string;
  userPromptTemplate: string;
  defaultProviderId: string | null;
  defaultModelId: string | null;
  corpusId: string | null;
  revisionComment: string;
  defaultPrompts: U3203DefaultPrompt[];
  /** Valeurs fusionnées org + overrides pour l'UI (non envoyées API). */
  parameterDisplayValues: Record<string, unknown>;
  /** Defaults org du modèle sélectionné (contexte UI, non envoyés API). */
  orgModelDefaultParameters: Record<string, unknown>;
};

export type U3203MissionRevisionChangeType = 'create' | 'update' | 'restore';

export interface U3203AiMissionRevisionSummary {
  revisionNumber: number;
  changeType: U3203MissionRevisionChangeType;
  comment?: string | null;
  restoredFromRevisionNumber?: number | null;
  createdBy: string;
  createdAt: string;
}

export interface U3203AiMissionRevision extends U3203AiMissionRevisionSummary {
  title: string;
  description?: string | null;
  systemPrompt: string;
  userPromptTemplate?: string | null;
  templateVariables?: string[];
  category: U3203MissionCategory;
  linkedLogicalUnit?: string | null;
  active: boolean;
  defaultProvider?: U3203ProviderRef | null;
  defaultModel?: U3203ModelRef | null;
  parameterOverrides?: Record<string, unknown>;
}

export interface U3203AiMissionRestoreBody {
  version: number;
  comment?: string | null;
}

export interface U3203AiMissionCreateBody {
  code: string;
  title: string;
  description?: string | null;
  systemPrompt: string;
  userPromptTemplate?: string | null;
  templateVariables?: string[];
  category: U3203MissionCategory;
  linkedLogicalUnit?: string | null;
  active: boolean;
  defaultProviderId?: string | null;
  defaultModelId?: string | null;
  corpusId?: string | null;
  parameterOverrides?: Record<string, unknown>;
}

export interface U3203AiMissionUpdateBody {
  id: string;
  title: string;
  description?: string | null;
  systemPrompt: string;
  userPromptTemplate?: string | null;
  templateVariables?: string[];
  category: U3203MissionCategory;
  linkedLogicalUnit?: string | null;
  active: boolean;
  defaultProviderId?: string | null;
  defaultModelId?: string | null;
  corpusId?: string | null;
  parameterOverrides?: Record<string, unknown>;
  version: number;
  revisionComment?: string | null;
}

export interface U3203AiMissionDeleteBody {
  id: string;
  version: number;
}

export const U3203_ERROR_CODE_SYSTEM_READ_ONLY = 'BU320301';
export const U3203_ERROR_CODE_REVISION_NOT_FOUND = 'BU320306';
export const U3203_ERROR_CODE_PARAMETER_INVALID = 'BU320203';
export const U3203_ERROR_CODE_PROVIDER_MODEL_INACTIVE = 'BU320307';
export const U3203_ERROR_CODE_OVERRIDES_WITHOUT_MODEL = 'BU320308';
export const U3203_ERROR_CODE_CORPUS_ORG_MISMATCH = 'BU320309';
export const U3203_ERROR_CODE_CORPUS_INACTIVE = 'BU320310';
export const U3203_ERROR_CODE_DEFAULT_PROMPT_LIMIT = 'BU320312';
export const U3203_ERROR_CODE_ORG_CONFIG_SYSTEM_ONLY = 'BU320313';
export const U3203_ERROR_CODE_PROVIDER_MODEL_REQUIRED = 'BU320311';

function u3203ResolveParameterOverridesForApi(data: U3203AiMissionFormRow): Record<string, unknown> {
  if (!data.defaultModelId) {
    return {};
  }
  const orgDefaults = data.orgModelDefaultParameters ?? {};
  if (Object.keys(orgDefaults).length > 0) {
    return u3203ExtractSparseOverrides(orgDefaults, data.parameterDisplayValues ?? {});
  }
  return data.parameterOverrides ?? {};
}

export function createEmptyU3203FormRow(): U3203AiMissionFormRow {
  return {
    id: '',
    code: '',
    title: '',
    description: '',
    systemPrompt: '',
    userPromptTemplate: '',
    templateVariables: [],
    category: 'assistant',
    linkedLogicalUnit: null,
    active: true,
    systemScope: false,
    revisionNumber: 1,
    defaultProviderId: null,
    defaultModelId: null,
    corpusId: null,
    parameterOverrides: {},
    parameterDisplayValues: {},
    orgModelDefaultParameters: {},
    defaultPrompts: [],
    version: 0,
    revisionComment: '',
  };
}

/** Nouvelle fiche — mission système si admin global sans organisation (BL-0388). */
export function createNewU3203FormRow(sessionOrganizationId: string | null | undefined): U3203AiMissionFormRow {
  return {
    ...createEmptyU3203FormRow(),
    systemScope: !u2000SessionHasOrganization(sessionOrganizationId),
  };
}

export function u3203IsPersistedMissionId(id: string | undefined | null): boolean {
  return Boolean(id) && id !== 'new' && SERVER_UUID_RE.test(id ?? '');
}

export function u3203NextDefaultPromptSortOrder(items: Pick<U3203DefaultPrompt, 'sortOrder'>[]): number {
  if (items.length === 0) return 1;
  return Math.max(...items.map((item) => item.sortOrder)) + 1;
}

export function u3203CountActiveDefaultPrompts(
  items: Array<{ id?: string; active: boolean }>,
  excludeId?: string,
): number {
  return items.filter((item) => item.active && (excludeId == null || item.id !== excludeId)).length;
}

export function buildDefaultPromptsBody(items: U3203DefaultPromptItemInput[]): U3203DefaultPromptsReplaceBody {
  return {
    items: items.map((item) => {
      const trimmed: U3203DefaultPromptItemInput = {
        labelFr: item.labelFr.trim(),
        labelEn: item.labelEn.trim(),
        promptFr: (item.promptFr ?? '').trim(),
        promptEn: (item.promptEn ?? '').trim(),
        sortOrder: item.sortOrder,
        active: item.active,
      };
      const id = item.id?.trim();
      if (id && SERVER_UUID_RE.test(id)) {
        trimmed.id = id;
      }
      return trimmed;
    }),
  };
}

export function u3203NormalizeDefaultPrompt(prompt: U3203DefaultPromptItemInput & { id?: string }): U3203DefaultPrompt {
  return {
    id: prompt.id ?? '',
    labelFr: prompt.labelFr ?? '',
    labelEn: prompt.labelEn ?? '',
    promptFr: prompt.promptFr ?? '',
    promptEn: prompt.promptEn ?? '',
    sortOrder: prompt.sortOrder,
    active: prompt.active,
  };
}

export function u3203DefaultPromptsFingerprint(items: U3203DefaultPrompt[]): string {
  return items
    .map(
      (item) =>
        `${item.id}:${item.sortOrder}:${item.active ? 1 : 0}:${item.labelFr}:${item.labelEn}:${item.promptFr}:${item.promptEn}`,
    )
    .join('|');
}

export function normalizeU3203AiMissionFromApi(row: U3203AiMission): U3203AiMissionFormRow {
  return {
    id: row.id,
    code: row.code,
    title: row.title,
    description: row.description ?? '',
    systemPrompt: row.systemPrompt,
    userPromptTemplate: row.userPromptTemplate ?? '',
    templateVariables: row.templateVariables ?? [],
    category: row.category,
    linkedLogicalUnit: row.linkedLogicalUnit ?? null,
    active: row.active,
    systemScope: row.systemScope,
    revisionNumber: row.revisionNumber,
    defaultProviderId: row.defaultProvider?.id ?? null,
    defaultModelId: row.defaultModel?.id ?? null,
    corpusId: row.corpusId ?? row.corpus?.id ?? null,
    parameterOverrides: row.parameterOverrides ?? {},
    parameterDisplayValues: {},
    orgModelDefaultParameters: {},
    defaultPrompts: (row.defaultPrompts ?? []).map(u3203NormalizeDefaultPrompt),
    version: row.version,
    revisionComment: '',
  };
}

export function buildCreateBody(data: U3203AiMissionFormRow): U3203AiMissionCreateBody {
  const desc = data.description.trim();
  const userTpl = data.userPromptTemplate.trim();
  const body: U3203AiMissionCreateBody = {
    code: data.code.trim().toUpperCase(),
    title: data.title.trim(),
    description: desc === '' ? null : desc,
    systemPrompt: data.systemPrompt,
    userPromptTemplate: userTpl === '' ? null : userTpl,
    category: data.category,
    linkedLogicalUnit: data.linkedLogicalUnit?.trim() ? data.linkedLogicalUnit.trim() : null,
    active: data.active,
    corpusId: data.corpusId,
  };
  if (!data.systemScope) {
    body.defaultProviderId = data.defaultProviderId;
    body.defaultModelId = data.defaultModelId;
    body.parameterOverrides = u3203ResolveParameterOverridesForApi(data);
  }
  if (data.templateVariables && data.templateVariables.length > 0) {
    body.templateVariables = data.templateVariables;
  }
  return body;
}

export function buildOrgConfigBody(data: U3203AiMissionFormRow): U3203OrgConfigReplaceBody {
  return {
    defaultProviderId: data.defaultProviderId,
    defaultModelId: data.defaultModelId,
    parameterOverrides: u3203ResolveParameterOverridesForApi(data),
  };
}

export function u3203OrgConfigFingerprint(
  data: Pick<
    U3203AiMissionFormRow,
    'defaultProviderId' | 'defaultModelId' | 'parameterOverrides' | 'parameterDisplayValues' | 'orgModelDefaultParameters'
  >,
): string {
  const overrides =
    Object.keys(data.orgModelDefaultParameters ?? {}).length > 0
      ? u3203ExtractSparseOverrides(data.orgModelDefaultParameters ?? {}, data.parameterDisplayValues ?? {})
      : (data.parameterOverrides ?? {});
  return `${data.defaultProviderId ?? 'null'}:${data.defaultModelId ?? 'null'}:${JSON.stringify(overrides)}`;
}

export function validateOrgConfigForm(
  row: U3203AiMissionFormRow,
  t: (key: string, opts?: { ns: string }) => string,
  ns: string,
): Record<string, string> {
  const result: Record<string, string> = {};
  const hasProvider = Boolean(row.defaultProviderId);
  const hasModel = Boolean(row.defaultModelId);
  if (hasProvider !== hasModel) {
    if (!hasProvider) {
      result['defaultProviderId'] = t('errDefaultProviderRequired', { ns });
    }
    if (!hasModel) {
      result['defaultModelId'] = t('errDefaultModelRequired', { ns });
    }
  }
  const overrides = u3203ResolveParameterOverridesForApi(row);
  if (!hasModel) {
    const hasStoredOverrides = Object.keys(row.parameterOverrides ?? {}).length > 0;
    const hasDisplayOverrides =
      Object.keys(row.orgModelDefaultParameters ?? {}).length > 0
        ? Object.keys(
            u3203ExtractSparseOverrides(row.orgModelDefaultParameters ?? {}, row.parameterDisplayValues ?? {}),
          ).length > 0
        : Object.keys(row.parameterDisplayValues ?? {}).length > 0;
    if (Object.keys(overrides).length > 0 || hasStoredOverrides || hasDisplayOverrides) {
      result['parameterOverrides'] = t('errOverridesWithoutModel', { ns });
    }
  }
  return result;
}

export function buildUpdateBody(data: U3203AiMissionFormRow): U3203AiMissionUpdateBody {
  const desc = data.description.trim();
  const userTpl = data.userPromptTemplate.trim();
  const body: U3203AiMissionUpdateBody = {
    id: data.id,
    title: data.title.trim(),
    description: desc === '' ? null : desc,
    systemPrompt: data.systemPrompt,
    userPromptTemplate: userTpl === '' ? null : userTpl,
    category: data.category,
    linkedLogicalUnit: data.linkedLogicalUnit?.trim() ? data.linkedLogicalUnit.trim() : null,
    active: data.active,
    corpusId: data.corpusId,
    version: data.version,
  };
  if (!data.systemScope) {
    body.defaultProviderId = data.defaultProviderId;
    body.defaultModelId = data.defaultModelId;
    body.parameterOverrides = u3203ResolveParameterOverridesForApi(data);
  }
  if (data.templateVariables && data.templateVariables.length > 0) {
    body.templateVariables = data.templateVariables;
  }
  const revisionComment = data.revisionComment?.trim() ?? '';
  if (revisionComment !== '') {
    body.revisionComment = revisionComment;
  }
  return body;
}

export function buildDeleteBody(data: U3203AiMissionFormRow): U3203AiMissionDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3203AiMissionCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3203AiMissionUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3203AiMissionDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export const doListRevisions = (axios: AxiosInstance, missionId: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get<{ results: U3203AiMissionRevisionSummary[] }>(LogicalUnitEndpoints.REVISIONS(missionId), { params });
};

export const doGetRevision = (axios: AxiosInstance, missionId: string | number, revisionNumber: number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get<{ result: U3203AiMissionRevision }>(LogicalUnitEndpoints.REVISION(missionId, revisionNumber), { params });
};

export const doRestoreRevision = (
  axios: AxiosInstance,
  missionId: string | number,
  revisionNumber: number,
  body: U3203AiMissionRestoreBody,
) => {
  return axios.post<{ result: U3203AiMission }>(LogicalUnitEndpoints.RESTORE(missionId, revisionNumber), body);
};

export const doReplaceDefaultPrompts = (
  axios: AxiosInstance,
  missionId: string | number,
  body: U3203DefaultPromptsReplaceBody,
) => {
  return axios.post<{ result: U3203AiMission }>(LogicalUnitEndpoints.DEFAULT_PROMPTS(missionId), body);
};

export const doReplaceOrgConfig = (
  axios: AxiosInstance,
  missionId: string | number,
  body: U3203OrgConfigReplaceBody,
) => {
  return axios.post<{ result: U3203AiMission }>(LogicalUnitEndpoints.ORG_CONFIG(missionId), body);
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U3203_ERROR_CODE_SYSTEM_READ_ONLY:
      errorResult.otherErrors = {
        ...(errorResult.otherErrors ?? {}),
        systemScope: t('errSystemMissionReadOnly', { ns: nameSpace }),
      };
      break;
    case U3203_ERROR_CODE_REVISION_NOT_FOUND:
      errorResult.genericErrors = [
        ...(errorResult.genericErrors ?? []),
        { title: t('errorTitle', { ns: nameSpace }), text: t('errRevisionNotFound', { ns: nameSpace }) },
      ];
      break;
    case U3203_ERROR_CODE_PARAMETER_INVALID:
      errorResult.genericErrors = [
        ...(errorResult.genericErrors ?? []),
        { title: t('errorTitle', { ns: nameSpace }), text: t('errParameterInvalid', { ns: nameSpace }) },
      ];
      break;
    case U3203_ERROR_CODE_PROVIDER_MODEL_INACTIVE:
      errorResult.genericErrors = [
        ...(errorResult.genericErrors ?? []),
        { title: t('errorTitle', { ns: nameSpace }), text: t('errProviderModelInactive', { ns: nameSpace }) },
      ];
      break;
    case U3203_ERROR_CODE_OVERRIDES_WITHOUT_MODEL:
      errorResult.genericErrors = [
        ...(errorResult.genericErrors ?? []),
        { title: t('errorTitle', { ns: nameSpace }), text: t('errOverridesWithoutModel', { ns: nameSpace }) },
      ];
      break;
    case U3203_ERROR_CODE_CORPUS_ORG_MISMATCH:
      errorResult.genericErrors = [
        ...(errorResult.genericErrors ?? []),
        { title: t('errorTitle', { ns: nameSpace }), text: t('errCorpusOrgMismatch', { ns: nameSpace }) },
      ];
      break;
    case U3203_ERROR_CODE_CORPUS_INACTIVE:
      errorResult.genericErrors = [
        ...(errorResult.genericErrors ?? []),
        { title: t('errorTitle', { ns: nameSpace }), text: t('errCorpusInactive', { ns: nameSpace }) },
      ];
      break;
    case U3203_ERROR_CODE_DEFAULT_PROMPT_LIMIT:
      errorResult.genericErrors = [
        ...(errorResult.genericErrors ?? []),
        { title: t('errorTitle', { ns: nameSpace }), text: t('errDefaultPromptLimit', { ns: nameSpace }) },
      ];
      break;
    case U3203_ERROR_CODE_ORG_CONFIG_SYSTEM_ONLY:
      errorResult.genericErrors = [
        ...(errorResult.genericErrors ?? []),
        { title: t('errorTitle', { ns: nameSpace }), text: t('errOrgConfigSystemOnly', { ns: nameSpace }) },
      ];
      break;
    case U3203_ERROR_CODE_PROVIDER_MODEL_REQUIRED:
      errorResult.genericErrors = [
        ...(errorResult.genericErrors ?? []),
        { title: t('errorTitle', { ns: nameSpace }), text: t('errorBU320311', { ns: nameSpace }) },
      ];
      break;
    default:
      break;
  }
}
